import { useOutletContext } from 'react-router-dom'

import { CompensationsTimeReimbursementsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import MapTimeCategoryWithPayrollConcept from 'modules/compensations/components/MapTimeCategoryWithPayrollConcept'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'

const TimeReimbursements = () => {
  const { company } = useCurrentSession()
  const { compensationsEnabled } = useOutletContext<{ compensationsEnabled: boolean }>()
  const { upsert: upsertCompensationMapping } = useUpsertReimbursablePayrollConcept()

  const { data, loading } = useQuery(CompensationsTimeReimbursementsDocument, {
    variables: { companyId: company.id },
  })

  if (loading) return <Loading />

  if (!compensationsEnabled) return <NotFound />

  const reimbursements = data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []
  const timeRangeCategories = data?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []

  const timeRangeCategoriesWithPayrollConcepts = timeRangeCategories.filter((category) =>
    reimbursements.some(
      (reimbursment) => reimbursment.reimbursableId === category.id && reimbursment.payrollConceptId
    )
  )

  const onRowChanged = async (timeCategoryId: number, payrollConceptId: number) => {
    const reimbursement = reimbursements.find((r) => r.reimbursableId === timeCategoryId)

    if (!reimbursement) {
      return
    }

    const result = await upsertCompensationMapping({
      id: reimbursement.id,
      payrollConceptId: payrollConceptId,
      reimbursableId: reimbursement.reimbursableId,
      reimbursableType: reimbursement.reimbursableType,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  return (
    <MapTimeCategoryWithPayrollConcept
      reimbursements={reimbursements}
      payrollConcepts={payrollConcepts}
      timeRangeCategories={timeRangeCategoriesWithPayrollConcepts}
      onRowChanged={onRowChanged}
    />
  )
}

export default TimeReimbursements
